import { useMemo, useState } from 'react';
import { X, Check, CheckCircle, ChevronDown, Loader2, Sparkles } from 'lucide-react';
import { useGetCheckOptionsQuery } from '../../store/api/apiSlice';
import type { CheckOption } from '../../types';
import { planAddons, planFeatures, priceSelection, visibleCheckOptions } from '../../utils/checkOptions';
import { readSelection } from '../../utils/planSelection';

interface SelectCheckOptionsModalProps {
    isOpen: boolean;
    onClose: () => void;
    // Receives the add-on ids the employer ticked, in catalog order.
    // Receives the plan features and the add-ons the employer chose.
    onSelectPackage: (
        packageId: string,
        features: string[],
        addons: string[],
        // Plan name and the charge for ONE person under this selection — the
        // bulk screen multiplies it to show a running total.
        meta: { planName: string; pricePerSubject: number },
    ) => void;
}

// Format price in Naira
function formatNaira(price: number): string {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(
        price,
    );
}

// Fallback data mirrors GET /api/v1/payments/check-options shape.
// Used only if the API is unreachable; live data is the source of truth.
// Kept in step with the admin Plans/Pricing screens so an outage never quotes
// a price or a feature list the product no longer honours.
const FALLBACK_OPTIONS: CheckOption[] = [
    {
        id: 'standard',
        name: 'Standard',
        price: 218500,
        currency: 'NGN',
        popular: false,
        features: [
            'Personal Identification & Verification',
            '1 Guarantor verification',
            'Criminal History checks at police SCID',
            'Employee Physical Address Verification and intelligence gathering',
            'Affidavit of undertaking',
            'Notarised Non-Disclosure Agreement',
            'Notarised Contract of Service.',
        ],
    },
    {
        id: 'enhanced',
        name: 'Premium',
        price: 400000,
        currency: 'NGN',
        popular: true,
        features: [
            'Personal Identification & Verification',
            'Criminal History checks at police SCID',
            'Previous Workplace Conduct (5 years work history)',
            'Social Media Reputation',
            'Employee Physical Address verification and intelligence gathering',
            '2 Guarantor verifications',
            'Affidavit of undertaking',
            'Notarised Non-Disclosure Agreement',
            'Notarised Contract of Service.',
        ],
    },
    {
        id: 'pro',
        name: 'Professional',
        price: 900000,
        currency: 'NGN',
        popular: false,
        features: [
            'Personal Identification & Verification',
            'Criminal History checks at police SCID',
            'Financial & Credit',
            'Directorship History',
            'Credit history',
            'Previous Workplace Conduct',
            'Social Media Reputation',
            'Conflict Of Interest',
            'Professional Discipline Check (may require longer await time for report)',
            'Employee Physical Address verification and intelligence gathering',
            '2 Guarantor verifications',
            'Affidavit of undertaking',
            'Notarised Non-Disclosure Agreement',
            'Notarised Contract of Service.',
        ],
    },
];

// Who each tier is best suited for. Display-only — the API doesn't send this,
// so it's keyed by plan id (with name as a fallback).
const RECOMMENDED_FOR: Record<string, string> = {
    standard: 'Recommended for domestic staff',
    enhanced: 'Recommended for domestic and corporate employees',
    pro: 'Recommended for senior executives',
};

function recommendedFor(option: CheckOption): string | undefined {
    return RECOMMENDED_FOR[option.id] ?? RECOMMENDED_FOR[option.name.toLowerCase()];
}

// Deduped union of every medical test across the category panels — used to show
// exactly which tests the add-on covers on the package card.
function medicalTestList(medical?: CheckOption['medicalAnalysis']): string[] {
    if (!medical?.tests) return [];
    const all = Object.values(medical.tests).flat().filter(Boolean) as string[];
    return Array.from(new Set(all));
}

function PricingCard({
    option,
    onSelect,
    isExpanded,
    onToggle,
}: {
    option: CheckOption;
    onSelect: (features: string[], addons: string[], pricePerSubject: number) => void;
    /** Only the chosen plan shows its lines; the rest stay one row tall. */
    isExpanded: boolean;
    onToggle: () => void;
}) {
    const isDark = option.popular;
    const recommendation = recommendedFor(option);
    const medical = option.medicalAnalysis;
    const medicalTests = medicalTestList(medical);
    // The plan's priced features, and the extras it can be topped up with.
    const features = useMemo(() => planFeatures(option), [option]);
    const addons = useMemo(() => planAddons(option), [option]);
    // A choice made on the landing page before signing in is rehydrated here,
    // so the visitor sees what they picked rather than starting again.
    // Otherwise everything is ticked: the whole plan is what we want them on,
    // and it is the cheapest way to get these features.
    const stashed = useMemo(() => {
        const saved = readSelection();
        return saved?.checkType === option.id ? saved : null;
    }, [option.id]);
    const [selectedFeatures, setSelectedFeatures] = useState<string[]>(
        () => stashed?.features ?? features.map((f) => f.id),
    );
    const [selectedAddons, setSelectedAddons] = useState<string[]>(() => stashed?.addons ?? []);
    // Backend exposes `employerPrice` (what this user actually pays) alongside `listPrice`
    // when the call is authenticated. Fall back to `price` for anonymous / older shapes.
    const effectivePrice = option.employerPrice ?? option.price;
    const listPrice = option.listPrice ?? option.price;
    // Show the discount UI whenever the two prices actually differ — don't gate on
    // `referralApplied` alone, otherwise a backend that sends the prices but forgets
    // the flag would silently hide a real discount from the user.
    const hasDiscount = listPrice > effectivePrice || option.referralApplied === true;
    const discountAmount = option.discountAmount ?? Math.max(listPrice - effectivePrice, 0);
    const discountRate = option.discountRate ?? (listPrice > 0 ? discountAmount / listPrice : 0);
    const discountPercentLabel = discountRate > 0 ? `${Math.round(discountRate * 100)}% off` : 'Referral discount';

    // Live maths, mirroring the server (POST /payments/quote), so the running
    // total updates on every tick without a round trip.
    const quote = priceSelection(option, selectedFeatures, selectedAddons);
    const toggleFeature = (id: string) =>
        setSelectedFeatures((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));
    const toggleAddon = (id: string) =>
        setSelectedAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
    const takeWholePlan = () => setSelectedFeatures(features.map((f) => f.id));
    // Ordered by the plan so the payload matches what the server stores.
    const featureIds = features.filter((f) => selectedFeatures.includes(f.id)).map((f) => f.id);
    const addonIds = addons.filter((a) => selectedAddons.includes(a.id)).map((a) => a.id);
    // Dropping a line loses the whole-plan saving, which usually leaves a
    // bigger bill than just taking the plan. Say so rather than letting them
    // discover it on the payment screen.
    const costsMoreThanPlan = !quote.isWholePlan && quote.featuresTotal > effectivePrice;

    return (
        <div
            className={`w-full overflow-hidden rounded-2xl border transition-colors ${
                isExpanded
                    ? 'border-[#f37b23] ' + (isDark ? 'bg-[#14171a]' : 'bg-white')
                    : 'border-[#e4e1da] bg-white hover:border-[#c9c4ba]'
            }`}
        >
            {/* Collapsed head: plan, who it is for, price. Choosing a plan and
                seeing what is in it are the same gesture — one row tall until
                you pick it. */}
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isExpanded}
                className="flex w-full items-center gap-3 px-4 py-3.5 text-left md:px-5"
            >
                <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                        isExpanded ? 'border-[#f37b23] bg-[#f37b23]' : 'border-[#d6d2c9] bg-white'
                    }`}
                >
                    {isExpanded && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                </span>

                <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                        <span
                            className={`text-base font-bold md:text-lg ${isExpanded && isDark ? 'text-white' : 'text-[#151515]'}`}
                        >
                            {option.name}
                        </span>
                        {isDark && (
                            <span className="rounded-full bg-[#f37b23] px-2 py-0.5 text-[10px] font-semibold tracking-wider text-white uppercase">
                                Most popular
                            </span>
                        )}
                    </span>
                    {recommendation && <span className="mt-0.5 block text-xs text-[#8a857c]">{recommendation}</span>}
                </span>

                <span className="shrink-0 text-right">
                    <span className="block text-base font-bold text-[#f37b23] tabular-nums md:text-xl">
                        {formatNaira(effectivePrice)}
                    </span>
                    {features.length > 0 && (
                        <span className="block text-[11px] text-[#a8a399]">{features.length} checks</span>
                    )}
                </span>

                <ChevronDown
                    className={`h-4 w-4 shrink-0 transition-transform ${
                        isExpanded ? 'rotate-180 text-[#f37b23]' : 'text-[#a8a399]'
                    } ${isExpanded && isDark ? 'text-white' : ''}`}
                    aria-hidden="true"
                />
            </button>

            {isExpanded && (
                <div className={`px-4 pb-5 md:px-5 ${isDark ? 'bg-[#14171a]' : 'bg-[#f5f5f5]'}`}>
                    <div className="flex flex-col gap-[14px] pt-4">
                        {hasDiscount && discountAmount > 0 ? (
                            <div className="flex flex-col gap-1.5">
                                <div className="flex items-center gap-2">
                                    <span
                                        className={`text-sm md:text-base font-medium line-through ${isDark ? 'text-white/40' : 'text-gray-400'}`}
                                    >
                                        {formatNaira(listPrice)}
                                    </span>
                                    <span className="inline-flex items-center rounded-full bg-[#f37b23] text-white text-[10px] md:text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5">
                                        {discountPercentLabel}
                                    </span>
                                </div>
                                <p className="text-2xl md:text-[36px] font-bold text-[#f37b23] tracking-[-0.96px] leading-tight md:leading-[52px]">
                                    {formatNaira(effectivePrice)}
                                </p>
                                <span
                                    className={`inline-flex items-center gap-1.5 self-start rounded-lg ${isDark ? 'bg-white/10 text-white' : 'bg-orange-50 text-[#a85015]'} text-xs font-medium px-2.5 py-1`}
                                >
                                    You save {formatNaira(discountAmount)} · Referral discount applied
                                </span>
                            </div>
                        ) : (
                            <p className="text-2xl md:text-[36px] font-bold text-[#f37b23] tracking-[-0.96px] leading-tight md:leading-[52px]">
                                {formatNaira(effectivePrice)}
                            </p>
                        )}
                        <div className={`w-full h-px ${isDark ? 'bg-white/20' : 'bg-[#d9d9d9]'}`} />
                        {/* What the plan is made of. Each line is priced and can be
                        unticked, but the whole plan is the cheapest way to buy
                        them, so everything starts ticked. */}
                        {features.length > 0 && (
                            <p className={`text-xs ${isDark ? 'text-white/60' : 'text-[#6b6b6b]'}`}>
                                All included |{' '}
                                <span className={`font-medium ${isDark ? 'text-white' : 'text-[#1b1b1b]'}`}>
                                    tap any line to remove it
                                </span>
                            </p>
                        )}
                        <div className="flex flex-col gap-2">
                            {features.length > 0
                                ? features.map((feature) => {
                                      const isOn = selectedFeatures.includes(feature.id);
                                      return (
                                          <button
                                              key={feature.id}
                                              type="button"
                                              onClick={() => toggleFeature(feature.id)}
                                              className={`flex w-full cursor-pointer items-start gap-[10px] rounded-lg px-2 py-1.5 text-left transition-colors ${
                                                  isOn
                                                      ? isDark
                                                          ? 'hover:bg-white/10'
                                                          : 'hover:bg-black/5'
                                                      : isDark
                                                        ? 'opacity-50 hover:opacity-80 hover:bg-white/10'
                                                        : 'opacity-50 hover:opacity-80 hover:bg-black/5'
                                              }`}
                                          >
                                              {/* A bordered square, not a tick glyph: it has
                                              to read as a checkbox at a glance, or
                                              nobody realises the lines are optional. */}
                                              <span
                                                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors ${
                                                      isOn
                                                          ? 'border-[#f37b23] bg-[#f37b23]'
                                                          : isDark
                                                            ? 'border-white/40 bg-transparent'
                                                            : 'border-gray-300 bg-white'
                                                  }`}
                                              >
                                                  {isOn && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                              </span>
                                              <span
                                                  className={`flex-1 text-sm leading-[22px] ${
                                                      isDark ? 'text-white' : 'text-[#1b1b1b]'
                                                  } ${isOn ? '' : 'line-through'}`}
                                              >
                                                  {feature.label}
                                              </span>
                                              <span
                                                  className={`shrink-0 text-xs font-medium tabular-nums mt-0.5 ${
                                                      isDark ? 'text-white/60' : 'text-[#6b6b6b]'
                                                  }`}
                                              >
                                                  {formatNaira(feature.price)}
                                              </span>
                                          </button>
                                      );
                                  })
                                : /* A response that predates priced features. */
                                  option.features.map((feature, i) => (
                                      <div key={i} className="flex items-start gap-[10px] px-2 py-1.5">
                                          <CheckCircle
                                              className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? 'text-green-400' : 'text-green-500'}`}
                                          />
                                          <span
                                              className={`text-sm leading-[22px] text-left ${isDark ? 'text-white' : 'text-[#1b1b1b]'}`}
                                          >
                                              {feature}
                                          </span>
                                      </div>
                                  ))}
                        </div>

                        {/* Dropping a line adds the markup, and below a certain point
                        that costs MORE than the plan. Say so here rather than
                        letting them find out at the payment screen. */}
                        {!quote.isWholePlan && features.length > 0 && (
                            <button
                                type="button"
                                onClick={takeWholePlan}
                                className={`flex items-start gap-2 rounded-xl border border-dashed px-3 py-2.5 text-left text-xs ${
                                    costsMoreThanPlan
                                        ? 'border-[#f37b23] bg-[#f37b23]/10'
                                        : isDark
                                          ? 'border-white/30'
                                          : 'border-[#d9d9d9]'
                                }`}
                            >
                                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#f37b23]" />
                                <span className={isDark ? 'text-white/80' : 'text-[#4e4d4d]'}>
                                    {costsMoreThanPlan ? (
                                        <>
                                            This costs {formatNaira(quote.featuresTotal - effectivePrice)} more than the
                                            full {option.name} plan, which is {option.wholePlanDiscountPercent ?? 15}%
                                            cheaper than buying these one by one.{' '}
                                            <span className="font-semibold">Take everything</span>
                                        </>
                                    ) : (
                                        <>
                                            Taking every line saves {option.wholePlanDiscountPercent ?? 15}%.{' '}
                                            <span className="font-semibold">Restore the full plan</span>
                                        </>
                                    )}
                                </span>
                            </button>
                        )}

                        {/* Extras sold on top of the plan, never marked up. */}
                        {addons.length > 0 && (
                            <div className="mt-1 flex flex-col gap-2">
                                <p
                                    className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-[#8b8b8b]'}`}
                                >
                                    Add-ons
                                </p>
                                {addons.map((addon) => {
                                    const isOn = selectedAddons.includes(addon.id);
                                    const isMedical = addon.id === 'medical_analysis';
                                    return (
                                        <div key={addon.id} className="flex flex-col gap-1.5">
                                            <button
                                                type="button"
                                                onClick={() => toggleAddon(addon.id)}
                                                className={`flex items-start gap-3 rounded-xl border px-3 py-3 text-left transition-colors ${
                                                    isOn
                                                        ? 'border-[#f37b23] bg-[#f37b23]/10'
                                                        : isDark
                                                          ? 'border-white/20 hover:border-white/40'
                                                          : 'border-[#e0e0e0] hover:border-[#c9c9c9]'
                                                }`}
                                            >
                                                <span
                                                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                                                        isOn
                                                            ? 'border-[#f37b23] bg-[#f37b23]'
                                                            : 'border-current opacity-60'
                                                    }`}
                                                >
                                                    {isOn && <CheckCircle className="h-4 w-4 text-white" />}
                                                </span>
                                                <span className="flex flex-col gap-0.5">
                                                    <span
                                                        className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-[#1b1b1b]'}`}
                                                    >
                                                        {addon.name} (+{formatNaira(addon.price)})
                                                    </span>
                                                    {addon.description && (
                                                        <span
                                                            className={`text-xs ${isDark ? 'text-white/60' : 'text-[#6b6b6b]'}`}
                                                        >
                                                            {addon.description}
                                                        </span>
                                                    )}
                                                </span>
                                            </button>

                                            {isMedical && medicalTests.length > 0 && (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {medicalTests.map((test) => (
                                                        <span
                                                            key={test}
                                                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${
                                                                isDark
                                                                    ? 'bg-white/10 text-white/80'
                                                                    : 'bg-[#f0f0f0] text-[#4e4d4d]'
                                                            }`}
                                                        >
                                                            {test}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Running total — what checkout will charge. */}
                        {features.length > 0 && (
                            <div
                                className={`mt-1 flex flex-col gap-1 rounded-xl px-3 py-2.5 ${isDark ? 'bg-white/10' : 'bg-[#ececec]'}`}
                            >
                                <div className="flex items-center justify-between text-xs">
                                    <span className={isDark ? 'text-white/70' : 'text-[#6b6b6b]'}>
                                        {quote.isWholePlan
                                            ? `All ${features.length} features`
                                            : `${quote.features.length} of ${features.length} features`}
                                    </span>
                                    <span className={isDark ? 'text-white/70' : 'text-[#6b6b6b]'}>
                                        {formatNaira(quote.subtotal)}
                                    </span>
                                </div>
                                {quote.discountAmount > 0 && (
                                    <div className="flex items-center justify-between text-xs font-medium text-[#f37b23]">
                                        <span>Whole plan saving ({quote.discountPercent}%)</span>
                                        <span>−{formatNaira(quote.discountAmount)}</span>
                                    </div>
                                )}
                                {quote.addonsTotal > 0 && (
                                    <div className="flex items-center justify-between text-xs">
                                        <span className={isDark ? 'text-white/70' : 'text-[#6b6b6b]'}>
                                            {quote.addons.length} add-on{quote.addons.length > 1 ? 's' : ''}
                                        </span>
                                        <span className={isDark ? 'text-white/70' : 'text-[#6b6b6b]'}>
                                            +{formatNaira(quote.addonsTotal)}
                                        </span>
                                    </div>
                                )}
                                <div
                                    className={`flex items-center justify-between border-t pt-1.5 text-sm font-bold ${
                                        isDark ? 'border-white/20 text-white' : 'border-[#d9d9d9] text-[#1b1b1b]'
                                    }`}
                                >
                                    <span>Total</span>
                                    <span>{formatNaira(quote.total)}</span>
                                </div>
                            </div>
                        )}
                        <button
                            onClick={() => onSelect(featureIds, addonIds, quote.total)}
                            className="mt-1 h-11 w-full rounded-xl bg-[#f37b23] text-sm font-semibold text-white transition-colors hover:bg-[#e06a15]"
                        >
                            Continue with {option.name}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function SelectCheckOptionsModal({ isOpen, onClose, onSelectPackage }: SelectCheckOptionsModalProps) {
    // Which plan is open. Null means none — the visitor picks a plan first and
    // only then sees what is in it, which is what keeps this one screen tall.
    const [expandedId, setExpandedId] = useState<string | null>(null);
    // Always refetch when the modal opens: /payments/check-options is referral-aware,
    // and we don't want to render stale anonymous list prices for a referred employer.
    const {
        data: checkOptions,
        isLoading,
        error,
    } = useGetCheckOptionsQuery(undefined, {
        skip: !isOpen,
        refetchOnMountOrArgChange: true,
    });

    if (!isOpen) return null;

    // Hide the internal "Test (Live Payment Check)" plan from the picker.
    const options = visibleCheckOptions(checkOptions && checkOptions.length > 0 ? checkOptions : FALLBACK_OPTIONS);

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-8">
            <div className="relative flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl bg-white shadow-[0px_4px_18px_0px_rgba(0,0,0,0.09)]">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 border-b border-[#efece7] px-5 py-4 md:px-6">
                    <div>
                        <h2 className="text-lg font-bold tracking-[-0.5px] text-[#151515] md:text-xl">
                            Select Check Options
                        </h2>
                        <p className="mt-0.5 text-xs text-[#8a857c]">
                            Pick a plan to see what it covers. You can remove any check you don't need.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#e8e8e8] transition-colors hover:bg-gray-300"
                    >
                        <X className="h-4 w-4 text-[#151515]" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4 md:px-6">
                    {/* Loading */}
                    {isLoading && (
                        <div className="flex items-center justify-center py-32">
                            <Loader2 className="w-10 h-10 text-[#f37b23] animate-spin" />
                        </div>
                    )}

                    {/* Error */}
                    {error && !isLoading && (
                        <div className="text-center py-8 text-sm text-red-500 mb-4">
                            Failed to load pricing. Showing default options.
                        </div>
                    )}

                    {/* Pricing Cards — wrap to new rows so any number of plans stays responsive */}
                    {!isLoading && (
                        <div className="flex flex-col gap-2.5">
                            {options.map((option) => (
                                <PricingCard
                                    key={option.id}
                                    option={option}
                                    isExpanded={expandedId === option.id}
                                    onToggle={() => setExpandedId((prev) => (prev === option.id ? null : option.id))}
                                    onSelect={(features, addons, pricePerSubject) =>
                                        onSelectPackage(option.id, features, addons, {
                                            planName: option.name,
                                            pricePerSubject,
                                        })
                                    }
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
